import Button from '@/components/Button.jsx';

import { MdMemory } from '@/components/react-icons/main';
import confirmDialog from '@/lib/confirmDialog.js';
import { AddIcon } from '@/lib/constants/icons.js';
import debounce from '@/lib/debounce.js';
import sendToast from '@/lib/sendToast.js';
import stepActions from '@/screens/workflows/redux/actions/stepActions.js';
import workflowScreenSelectors from '@/screens/workflows/redux/selectors/workflowScreenSelectors.js';
import { randomId } from '@shared/utils/randomId.js';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import SliderClose from './SliderClose.jsx';
import SliderHeader from './SliderHeader.jsx';
import SliderTitle from './SliderTitle.jsx';
import MemorySliderItem from './MemorySliderItem.jsx';

const MemoriesSlider = ({ onClose, disabled = false }) => {
  const activeStep = useSelector(workflowScreenSelectors.activeWorkflowStep);
  const stepId = activeStep?.id;
  const memories = activeStep?.options?.memory || [];
  const [localMemories, setLocalMemories] = useState(memories);
  const localMemoriesRef = useRef(memories);
  const writersRef = useRef(new Map());

  // The open slider owns the entries it is editing: redux is the sink, never the source.
  // Seeding it from redux on every step emission instead loses keystrokes, because a write is
  // only debounced-then-cooked and the emissions it triggers land while the user is still
  // typing. Adds and deletes are applied to both sides so the list stays in step.
  useEffect(() => {
    const openedMemories = activeStep?.options?.memory || [];
    setLocalMemories(openedMemories);
    localMemoriesRef.current = openedMemories;
    writersRef.current = new Map();
  }, [stepId]);

  const applyLocalMemories = (updatedMemories) => {
    localMemoriesRef.current = updatedMemories;
    setLocalMemories(updatedMemories);
  };

  const getLabel = (value, options) => {
    const option = options.find((o) => o.value === value);
    return option?.label || value;
  };

  /**
   * One debounced writer per entry, each flushing its entry in full. A writer shared across
   * entries cancels the pending flush of the entry edited before it, and a partial
   * `{ field: value }` payload cancels the pending flush of the field edited before it — which
   * is how typing a Key and then setting a Value discarded the Key outright.
   */
  const getMemoryWriter = (memoryId) => {
    if (!writersRef.current.has(memoryId)) {
      const writer = debounce((updatedMemory) => {
        stepActions.set('options.memory', (options) => (Array.isArray(options) ? options : []).map((option) => (option.id === memoryId ? { ...option, ...updatedMemory } : option)), { stepId });
      }, 500);
      writersRef.current.set(memoryId, writer);
    }

    return writersRef.current.get(memoryId);
  };

  const handleChange = (memoryId, field, value) => {
    const memory = localMemoriesRef.current.find((m) => m?.id === memoryId);
    if (field !== 'key' && !memory?.key) {
      sendToast({ message: 'Please enter a key for this memory', type: 'error' });
      return;
    }

    const updatedMemory = { ...memory, [field]: value };
    applyLocalMemories(localMemoriesRef.current.map((m) => (m?.id === memoryId ? updatedMemory : m)));
    getMemoryWriter(memoryId)(updatedMemory);
  };

  const handleDelete = async (memoryId) => {
    const confirmed = await confirmDialog({
      title: 'Delete Memory',
      message: 'Are you sure you want to delete this memory?',
      confirm: 'Delete'
    });

    if (confirmed) {
      applyLocalMemories(localMemoriesRef.current.filter((m) => m?.id !== memoryId));
      stepActions.set('options.memory', (options) => (Array.isArray(options) ? options : []).filter((option) => option.id !== memoryId), { stepId });
    }
  };

  const handleAdd = () => {
    const newMemory = {
      id: randomId(),
      key: '',
      name: '',
      savesOn: 'success',
      expiresOn: 'never',
      value: ''
    };

    applyLocalMemories([...localMemoriesRef.current, newMemory]);
    stepActions.set('options.memory', (options) => [...(Array.isArray(options) ? options : []), newMemory], { stepId });
  };

  return (
    <>
      <SliderHeader onClose={onClose} disabled={disabled} />
      <SliderTitle icon={MdMemory} title="Edit Memory Entries" description="Configure memory settings for all entries." helpIconContext="workflowStepMemoryContext" />

      {localMemories.map((memory) => (
        <MemorySliderItem key={memory.id} memory={memory} handleDelete={handleDelete} handleChange={handleChange} getLabel={getLabel} disabled={disabled} />
      ))}
      <Button className="full-width" size="xl" icon={AddIcon} onClick={handleAdd} disabled={disabled}>
        Add Memory Entry
      </Button>
      <SliderClose onClose={onClose} />
    </>
  );
};

export default MemoriesSlider;
